import React, { Fragment, useEffect, useState } from 'react';
import AdminPageLoader from './AdminPageLoader';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FaRegEdit, FaPlus, FaSearch, FaImage, FaSpinner, FaTags } from "react-icons/fa";
import { MdDelete } from "react-icons/md";

function BrandsPage() {
    const [brandList, setBrandList] = useState([]);
    const [filteredBrands, setFilteredBrands] = useState([]);
    const [brandData, setBrandData] = useState({ name: '', image: '' });
    const [imageUploading, setImageUploading] = useState(false);
    const [savingBrand, setSavingBrand] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';
    const token = localStorage.getItem('authToken');

    useEffect(() => {
        handleBrandList();
    }, []);

    // Fetch all brands
    const handleBrandList = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/branddata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const brands = response.data || [];
            setBrandList(brands);
            setFilteredBrands(brands);
        } catch (error) {
            alert("Failed to fetch brands. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Search brand with debouncing
    const handleSearchBrand = (event) => {
        const search = event.target.value.toLowerCase();
        setSearchTerm(search);
        
        if (search) {
            const filtered = brandList.filter(brand => 
                brand.name.toLowerCase().includes(search)
            );
            setFilteredBrands(filtered);
        } else {
            setFilteredBrands(brandList);
        }
    };

    // Handle image upload
    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only JPG, PNG, WebP, or AVIF images are allowed.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert('File size must be under 5MB.');
            return;
        }
        try {
            setImageUploading(true);
            const formData = new FormData();
            formData.append('image', file);
            const response = await axios.post(`${API_URL}/upload-prod-img`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrandData({ ...brandData, image: response.data.path });
            alert("Image uploaded successfully!");
        } catch (error) {
            alert("Image upload failed. Please try again.");
        } finally {
            setImageUploading(false);
        }
    };

    // Handle form input changes
    const handleChange = (event) => {
        setBrandData({ ...brandData, [event.target.name]: event.target.value });
    };

    // Validate input fields
    const validateFields = () => {
        if (!brandData.name.trim()) {
            alert("Brand name is required.");
            return false;
        }
        if (!brandData.image.trim()) {
            alert("Brand image is required.");
            return false;
        }
        return true;
    };

    // Add a new brand
    const handleAddBrand = async () => {
        if (!validateFields()) return;

        try {
            setSavingBrand(true);
            const response = await axios.post(`${API_URL}/add-brand`, brandData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            handleBrandList();
            setBrandData({ name: '', image: '' });
            // Close modal using data-bs-dismiss attribute
            const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
            if (closeButton) {
                closeButton.click();
            }
        } catch (error) {
            alert("Failed to add brand. Please try again.");
        } finally {
            setSavingBrand(false);
        }
    };

    // Delete a brand
    const handleDeleteBrand = async (id) => {
        if (!window.confirm("Are you sure you want to delete this brand?")) return;

        try {
            await axios.delete(`${API_URL}/delete-brand/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("Brand deleted successfully!");
            handleBrandList();
        } catch (error) {
            alert("Failed to delete brand. Please try again.");
        }
    };

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">BRAND MANAGEMENT</h4>
                                <p className="text-muted p-0 m-0">Manage your product brands and categories</p>
                            </div>
                            <button 
                                type="button" 
                                className="btn theme-btn-blue-flat btn-sm rounded-0"
                                style={{
                                    padding: '6px 16px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                                onClick={() => setShowAddModal(true)}
                            >
                                <FaPlus size={11} />
                                Add Brand
                            </button>
                        </div>
                    </div>
                </div>

                {/* Control Panel — cloned from Pending Orders page */}
                <div className="control-panel-bar">
                    <div className="control-panel-search">
                        <span
                            className="position-absolute top-50 translate-middle-y start-0 ps-3 text-muted"
                            style={{ pointerEvents: 'none' }}
                        >
                            <FaSearch style={{ fontSize: '14px' }} />
                        </span>
                        <input
                            type="text"
                            className="form-control form-control-sm ps-5"
                            placeholder="Search by brand name..."
                            style={{
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                height: '38px',
                                fontSize: '14px',
                                background: '#ffffff',
                                width: '100%'
                            }}
                            value={searchTerm}
                            onChange={handleSearchBrand}
                        />
                    </div>
                    <div className="control-panel-reset" style={{ marginLeft: 'auto' }}>
                        <small className="text-muted" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                            Showing {filteredBrands.length} of {brandList.length} brands
                        </small>
                    </div>
                </div>

                {loading ? (
                    <AdminPageLoader message="Loading brands..." />
                ) : (
                    <div className="brands-grid-wrap">
                        {filteredBrands.length > 0 ? (
                            filteredBrands.map((data) => (
                                <div className="brands-grid-item" key={data._id}>
                                    <div className="brand-flat-card">
                                        <div className="brand-flat-img-wrap">
                                            <img
                                                src={`${API_URL}/${data.image}`}
                                                alt={data.name}
                                                className="brand-flat-img"
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/260x130?text=No+Image';
                                                }}
                                            />
                                        </div>
                                        <div className="brand-flat-body">
                                            <span className="brand-flat-name">{data.name}</span>
                                            <div className="brand-flat-actions">
                                                <Link
                                                    to={`/admin/brands/edit/${data._id}`}
                                                    className="brand-action-btn"
                                                    title="Edit Brand"
                                                >
                                                    <FaRegEdit size={13} />
                                                </Link>
                                                <button
                                                    onClick={() => handleDeleteBrand(data._id)}
                                                    className="brand-action-btn brand-action-btn--danger"
                                                    title="Delete Brand"
                                                >
                                                    <MdDelete size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div style={{ width: '100%' }}>
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body text-center py-5">
                                        <FaImage className="text-muted fs-1 mb-3" />
                                        <h5 className="text-muted">
                                            {searchTerm ? 'No brands found matching your search.' : 'No brands found'}
                                        </h5>
                                        <p className="text-muted">
                                            {searchTerm ? 'Try adjusting your search terms.' : 'Add your first brand to get started.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                )}
            </div>

            {/* Add Brand Modal — assign-driver style */}
            {showAddModal && (
                <div className="assign-modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="assign-modal-content" onClick={e => e.stopPropagation()}>

                        {/* Header */}
                        <div className="assign-modal-header">
                            <div className="d-flex align-items-center gap-2">
                                <div style={{
                                    width: '32px', height: '32px', borderRadius: '8px',
                                    background: '#e8eaf6', color: '#1a237e',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontSize: '15px'
                                }}>
                                    <FaTags />
                                </div>
                                <div className="d-flex flex-column align-items-start">
                                    <h5 style={{ color: '#0f172a', fontSize: '16px', fontWeight: '700', margin: 0 }}>
                                        Add New Brand
                                    </h5>
                                    <span style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>
                                        Fill in the details to add a new brand
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowAddModal(false)}
                                style={{
                                    border: 'none', background: 'none', fontSize: '20px',
                                    color: '#94a3b8', cursor: 'pointer', padding: '0 2px',
                                    lineHeight: 1, transition: 'color 0.2s'
                                }}
                                onMouseOver={e => e.currentTarget.style.color = '#0f172a'}
                                onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Body */}
                        <div className="assign-modal-body">
                            <span style={{ fontSize: '10px', fontWeight: '600', letterSpacing: '1px', color: '#94a3b8', marginBottom: '12px', display: 'block' }}>
                                BRAND DETAILS
                            </span>
                            <div className="mb-3">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Brand Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', height: '38px' }}
                                    placeholder="Enter brand name"
                                    name="name"
                                    value={brandData.name}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="mb-1">
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>Brand Image</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    style={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px', padding: '7px 12px' }}
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                />
                                {imageUploading && (
                                    <div className="mt-2 d-flex align-items-center gap-1">
                                        <FaSpinner style={{ fontSize: '11px', color: '#94a3b8' }} />
                                        <small style={{ color: '#94a3b8', fontSize: '11px' }}>Uploading image...</small>
                                    </div>
                                )}
                                {brandData.image && !imageUploading && (
                                    <div className="mt-2 d-flex align-items-center gap-1">
                                        <FaImage style={{ fontSize: '11px', color: '#16a34a' }} />
                                        <small style={{ color: '#16a34a', fontSize: '11px' }}>Image uploaded successfully</small>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="assign-modal-footer">
                            <button
                                type="button"
                                onClick={() => setShowAddModal(false)}
                                style={{
                                    flex: 1, padding: '8px 16px', borderRadius: '6px',
                                    border: '1px solid #e2e8f0', background: '#ffffff',
                                    color: '#374151', fontSize: '13px', fontWeight: '500',
                                    cursor: 'pointer', transition: 'all 0.2s'
                                }}
                                onMouseOver={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleAddBrand}
                                disabled={savingBrand || imageUploading}
                                style={{
                                    flex: 1, padding: '8px 16px', borderRadius: '6px',
                                    border: 'none', background: savingBrand || imageUploading ? '#c7d2fe' : '#1a237e',
                                    color: savingBrand || imageUploading ? '#6366f1' : '#ffff01',
                                    fontSize: '13px', fontWeight: '600',
                                    cursor: savingBrand || imageUploading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: '6px'
                                }}
                            >
                                {savingBrand ? (
                                    <><FaSpinner style={{ fontSize: '11px' }} /> Saving...</>
                                ) : 'Save Brand'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .brand-card {
                    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
                    border-radius: 8px;
                    overflow: hidden;
                }
                
                .brand-card:hover {
                    
                    
                }
                
                .brand-image-container {
                    position: relative;
                    overflow: hidden;
                }
                
                .brand-image {
                    height: 200px;
                    object-fit: cover;
                    transition: transform 0.3s ease;
                }
                
                .brand-card:hover .brand-image {
                    transform: scale(1.05);
                }
                
                .brand-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.7);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }
                
                .brand-card:hover .brand-overlay {
                    opacity: 1;
                }
                
                .brand-actions {
                    display: flex;
                    gap: 8px;
                }
                
                .brand-actions .btn {
                    border-radius: 4px;
                    font-weight: 500;
                    padding: 8px 16px;
                    border: none;
                    transition: all 0.2s ease;
                }
                
                .brand-actions .btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
                }
                
                .card-body {
                    padding: 1.25rem;
                    background: #fff;
                }
            `}</style>
        </Fragment>
    );
}

export default BrandsPage;
